import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import { EmployeeDialog } from './EmployeeDialog';
import api from '../../lib/api';
import type { Employee } from '../../types';
import { formatDate } from '../../lib/utils';
import {
  Card, CardContent, CardHeader,
  Button, Input, Badge,
} from '@werkstatt/ui';
import { Plus, Search, Pencil, UserX, UserCheck } from 'lucide-react';

export default function Employees() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState<Employee | null>(null);

  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['employees', showInactive],
    queryFn: async () => {
      const params = showInactive ? '' : '?is_active=true';
      const res = await api.get<Employee[]>(`/employees/${params}`);
      return res.data;
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.put(`/employees/${id}`, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Mitarbeiter aktualisiert');
    },
  });

  const filtered = employees.filter((e) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      e.first_name.toLowerCase().includes(s) ||
      e.last_name.toLowerCase().includes(s) ||
      (e.employee_number || '').toLowerCase().includes(s) ||
      (e.department || '').toLowerCase().includes(s)
    );
  });

  const handleEdit = (emp: Employee) => {
    setEditEmployee(emp);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditEmployee(null);
    setDialogOpen(true);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold">Mitarbeiter</h1>
            <p className="text-muted-foreground mt-1">Mitarbeiterverwaltung</p>
          </div>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4 mr-2" />
            Mitarbeiter anlegen
          </Button>
        </div>

        <Card variant="glass">
          <CardHeader>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 min-w-60">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suchen nach Name, Personalnr., Abteilung..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant={showInactive ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowInactive(!showInactive)}
              >
                {showInactive ? 'Alle anzeigen' : 'Inaktive einblenden'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-muted-foreground text-sm py-8 text-center">Lade...</p>
            ) : filtered.length === 0 ? (
              <p className="text-muted-foreground text-sm py-8 text-center">Keine Mitarbeiter gefunden</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-3 pr-4 font-medium">Name</th>
                      <th className="py-3 pr-4 font-medium">Personalnr.</th>
                      <th className="py-3 pr-4 font-medium">Position</th>
                      <th className="py-3 pr-4 font-medium">Abteilung</th>
                      <th className="py-3 pr-4 font-medium">Soll-Std/W</th>
                      <th className="py-3 pr-4 font-medium">Eintritt</th>
                      <th className="py-3 pr-4 font-medium">Status</th>
                      <th className="py-3 font-medium">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((emp) => (
                      <tr key={emp.id} className="border-b hover:bg-muted/30">
                        <td className="py-3 pr-4 font-medium">
                          {emp.last_name}, {emp.first_name}
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground">{emp.employee_number || '—'}</td>
                        <td className="py-3 pr-4">{emp.position || '—'}</td>
                        <td className="py-3 pr-4">{emp.department || '—'}</td>
                        <td className="py-3 pr-4">{emp.hours_per_week} Std</td>
                        <td className="py-3 pr-4 text-muted-foreground">
                          {emp.start_date ? formatDate(emp.start_date) : '—'}
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant={emp.is_active ? 'success' : 'secondary'}>
                            {emp.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(emp)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActiveMutation.mutate({ id: emp.id, is_active: !emp.is_active })}
                            >
                              {emp.is_active ? <UserX className="h-4 w-4 text-destructive" /> : <UserCheck className="h-4 w-4 text-green-600" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <EmployeeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} employee={editEmployee} />
    </MainLayout>
  );
}
